import React from "react";
import Navigation from "@/layout/navigation";

export default function PrivacyPolicy() {
  return (
    <>
      {/* Header avec navigation */}
      <header className="bg-gradient-to-b from-white from-20% to-blue-300 flex flex-col gap-16 items-center justify-between overflow-hidden px-8 xl:px-0 z-[51] relative pb-20">
        <Navigation noMenu />
        <div className="flex flex-col md:items-center md:justify-center gap-4 mt-6 md:mt-10 z-10 md:text-center">
          <h1 className="text-4xl md:text-5xl font-medium leading-tight">
            Privacy Policy
          </h1>
          <p className="text-md max-w-2xl mx-auto text-slate-600">
            Your privacy is important to us. This policy explains how we
            collect, use, and protect your personal information.
          </p>
        </div>
      </header>

      {/* Contenu principal - ultra simple */}
      <div className="bg-white py-20 px-8 xl:px-0">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-12 text-lg leading-relaxed text-gray-800">
            <div className="text-sm text-blue-600 font-medium">
              Last updated: September 28, 2025
            </div>

            <div className="space-y-8">
              <p>
                Featcal (&quot;we,&quot; &quot;our,&quot; &quot;us&quot;) is
                committed to protecting your privacy and ensuring compliance
                with applicable privacy laws, including Canada&apos;s Personal
                Information Protection and Electronic Documents Act (PIPEDA) and
                Québec&apos;s Act Respecting the Protection of Personal
                Information in the Private Sector (Law 25), as well as other
                international regulations where applicable (such as GDPR).
              </p>

              <p>
                This Privacy Policy explains how we collect, use, disclose, and
                safeguard your information when you use our platform, website
                (featcal.com), mobile applications, and related services
                (collectively, the &quot;Services&quot;).
              </p>

              <p className="font-semibold">
                By using Featcal, you consent to the practices described in this
                Privacy Policy.
              </p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                1. Information We Collect
              </h2>

              <div className="space-y-6">
                <h3 className="text-xl font-medium text-gray-900">
                  1.1 Personal Information
                </h3>
                <p>Name, email address, phone number, and login credentials</p>
                <p>Payment and billing information (if applicable)</p>
                <p>
                  Professional or organizational details (for doctors,
                  nutritionists, or coaches)
                </p>
              </div>

              <div className="space-y-6">
                <h3 className="text-xl font-medium text-gray-900">
                  1.2 Health & Wellness Information
                </h3>
                <p>
                  Body measurements and metrics (e.g., weight, BMI, body fat,
                  hydration, sleep, steps, heart rate)
                </p>
                <p>
                  Nutrition data (food logs, photos, dietary preferences,
                  allergies, meal plans)
                </p>
                <p>
                  Fitness information (workout plans, activities, consultation
                  notes)
                </p>
                <p>
                  Device integration data from third parties such as InBody,
                  Seca, Omron, and Tanita
                </p>
              </div>

              <div className="space-y-6">
                <h3 className="text-xl font-medium text-gray-900">
                  1.3 Automatically Collected Information
                </h3>
                <p>
                  Device information (hardware model, operating system, browser
                  type)
                </p>
                <p>
                  Usage data (logins, feature use, pages visited, actions within
                  the app)
                </p>
                <p>
                  IP address, location (if enabled), cookies or similar
                  technologies
                </p>
              </div>

              <div className="space-y-6">
                <h3 className="text-xl font-medium text-gray-900">
                  1.4 Communication Data
                </h3>
                <p>
                  Messages, notes, or consultation summaries exchanged between
                  professionals and clients
                </p>
                <p>Responses from AI-powered chatbot interactions</p>
              </div>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                2. How We Use Your Information
              </h2>

              <p>We use your information to:</p>

              <p>
                Provide and personalize the Services (meal plans, workout plans,
                progress tracking)
              </p>
              <p>
                Facilitate integrations with third-party health devices and
                platforms
              </p>
              <p>
                Automate nutrition analysis, consultation summaries, and
                progress reports using AI
              </p>
              <p>Send push notifications, reminders, and engagement messages</p>
              <p>
                Support secure communication between professionals and their
                patients or clients
              </p>
              <p>Process payments, subscriptions, and account management</p>
              <p>
                Improve performance, functionality, and security of the Services
              </p>
              <p>
                Conduct analytics and generate anonymized insights for product
                improvement
              </p>
              <p>
                Meet regulatory and compliance requirements (including PIPEDA
                and Law 25)
              </p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                3. Consent
              </h2>

              <p>
                We will always request explicit consent before collecting,
                using, or sharing sensitive personal information such as health
                data.
              </p>
              <p>
                Consent is requested at the time of registration and can be
                withdrawn at any time by contacting us (see Section 10).
              </p>
              <p>
                If you withdraw consent, certain features of the Services may no
                longer be available.
              </p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                4. Sharing of Information
              </h2>

              <p className="font-semibold">
                We do not sell your personal information.
              </p>
              <p>We may share your data in the following cases:</p>

              <div className="space-y-6">
                <div>
                  <p className="font-medium">
                    With Healthcare Providers and Coaches:
                  </p>
                  <p>
                    If you are a patient or client, your information may be
                    shared with your assigned professional for the purposes of
                    care, coaching, and progress tracking.
                  </p>
                </div>

                <div>
                  <p className="font-medium">Service Providers:</p>
                  <p>
                    With trusted vendors who perform services on our behalf
                    (hosting, analytics, payment processing, cloud storage, AI
                    models).
                  </p>
                </div>

                <div>
                  <p className="font-medium">Integrations:</p>
                  <p>
                    When you choose to connect external devices or services
                    (InBody, Seca, Omron, Tanita), we share the necessary data
                    to enable these integrations.
                  </p>
                </div>

                <div>
                  <p className="font-medium">Legal Obligations:</p>
                  <p>If required by law, regulation, or valid legal request.</p>
                </div>

                <div>
                  <p className="font-medium">Business Transfers:</p>
                  <p>
                    In the event of a merger, acquisition, or sale of assets.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                5. International Data Transfers
              </h2>

              <p>
                Your information may be processed and stored outside Québec and
                Canada, including in the United States and other jurisdictions.
              </p>
              <p>
                When this occurs, we ensure appropriate safeguards are in place,
                such as standard contractual clauses, data protection
                agreements, or equivalent legal protections, in compliance with
                Law 25.
              </p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                6. Data Security
              </h2>

              <p>
                We implement industry-standard safeguards to protect your
                information, including:
              </p>

              <p>Encryption in transit and at rest</p>
              <p>Role-based access controls and secure authentication</p>
              <p>Regular monitoring and vulnerability testing</p>
              <p>
                Healthcare-grade security infrastructure for handling
                health-related data
              </p>

              <p>
                While we take reasonable steps to protect your data, no system
                is 100% secure.
              </p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                7. Data Retention
              </h2>

              <p>
                We retain personal and health data only as long as necessary to
                fulfill the purposes described in this policy, or as required by
                law.
              </p>
              <p>
                Users may request deletion of their account and associated data
                at any time (subject to legal or medical record retention
                requirements).
              </p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                8. Your Rights
              </h2>

              <p>
                In accordance with PIPEDA, Law 25, and other applicable laws,
                you have the right to:
              </p>

              <p>Access and obtain a copy of your data</p>
              <p>Request correction or deletion of your data</p>
              <p>Restrict or object to processing</p>
              <p>Withdraw consent at any time</p>
              <p>
                Receive your data in a structured, portable format (data
                portability)
              </p>
              <p>
                File a complaint with the Office of the Privacy Commissioner of
                Canada or the Commission d&apos;accès à l&apos;information du
                Québec
              </p>

              <p className="font-semibold">
                Requests can be made to privacy@featcal.com
              </p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                9. Children&apos;s Privacy
              </h2>

              <p>
                Featcal is not intended for individuals under 18 years of age.
              </p>
              <p>We do not knowingly collect information from minors.</p>
              <p>If such information is discovered, it will be deleted.</p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                10. Privacy Officer
              </h2>

              <p>
                In compliance with Law 25, Featcal has appointed a Privacy
                Officer responsible for oversight and compliance.
              </p>

              <p className="font-semibold">Privacy Officer</p>
              <p>Email: privacy@featcal.com</p>
              <p>Address: [Insert Business Address]</p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                11. Changes to this Privacy Policy
              </h2>

              <p>We may update this Privacy Policy from time to time.</p>
              <p>Changes will be posted on this page with the updated date.</p>
              <p>
                Significant updates will be communicated through email or in-app
                notifications.
              </p>
            </div>

            <div className="space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-16 mb-8">
                12. Contact Us
              </h2>

              <p>
                If you have questions or requests regarding this Privacy Policy
                or how your information is handled, you can contact us at:
              </p>

              <p className="font-semibold">Featcal</p>
              <p>Email: privacy@featcal.com</p>
              <p>Website: https://featcal.com</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
